(function (root, factory) {
  'use strict';
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root && root.document) api.install(root);
})(typeof window !== 'undefined' ? window : null, function () {
  'use strict';

  const UNIT_PRICE_CENTS = 1000;
  const MAX_CODE_POINTS = 200;
  const whitespace = /^\s+$/u;

  function countCharacters(value) {
    const text = String(value == null ? '' : value).normalize('NFC');
    if (!text || whitespace.test(text)) return { text: '', characters: 0, codePoints: 0 };
    if (typeof Intl.Segmenter !== 'function') throw new Error('Your browser cannot verify the engraving price. Please update your browser or clear the engraving to continue.');
    let characters = 0;
    for (const part of new Intl.Segmenter('en', { granularity: 'grapheme' }).segment(text)) {
      if (!whitespace.test(part.segment)) characters += 1;
    }
    return { text: text, characters: characters, codePoints: Array.from(text).length };
  }

  function evaluateSelection(value, config) {
    config = config || {};
    const result = { text: '', characters: 0, unitPriceCents: UNIT_PRICE_CENTS, costCents: 0, variantId: '', valid: true, error: '' };
    try {
      const counted = countCharacters(value);
      Object.assign(result, { text: counted.text, characters: counted.characters, costCents: counted.characters * UNIT_PRICE_CENTS });
      if (!counted.characters) return result;
      const limit = Number.isInteger(config.maxCodePoints) && config.maxCodePoints > 0 ? Math.min(config.maxCodePoints, MAX_CODE_POINTS) : MAX_CODE_POINTS;
      if (counted.codePoints > limit) throw new Error('This text is too long. Please shorten it to ' + limit + ' characters or fewer.');
      if (/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F-\u009F]/u.test(counted.text)) throw new Error('Remove unsupported control characters from the engraving.');
      const variantId = String(config.variantId || '');
      if (config.available !== true || !/^\d{8,20}$/.test(variantId) || Number(config.unitPriceCents) !== UNIT_PRICE_CENTS || config.currency !== 'USD') {
        throw new Error('Engraving is temporarily unavailable. Clear the text to continue without engraving.');
      }
      result.variantId = variantId;
    } catch (error) {
      result.valid = false;
      result.error = error.message || 'We could not verify the engraving. Clear the text to continue.';
    }
    return result;
  }

  function install(win) {
    const doc = win.document;
    let field = null;
    let observer = null;
    function config() {
      try { return JSON.parse(doc.getElementById('ds-engraving-config')?.textContent || '{}'); }
      catch (_) { return {}; }
    }
    function getSelection() { return evaluateSelection(field ? field.value : '', config()); }
    function money(cents) { return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100); }
    function renderFeedback(announce) {
      if (!field) return;
      const selected = getSelection();
      const count = doc.getElementById('ds-engraving-count');
      const fee = doc.getElementById('ds-engraving-fee');
      const error = doc.getElementById('ds-engraving-error');
      const clear = doc.getElementById('ds-engraving-clear');
      count.textContent = selected.characters + ' paid character' + (selected.characters === 1 ? '' : 's');
      fee.textContent = selected.characters ? '+' + money(selected.costCents) : money(0);
      clear.hidden = !field.value;
      error.textContent = selected.error;
      error.hidden = selected.valid;
      field.setAttribute('aria-invalid', String(!selected.valid));
      field.setCustomValidity(selected.error);
      if (announce) doc.dispatchEvent(new win.CustomEvent('ds:engraving', { detail: selected }));
    }
    function mount() {
      if (field?.isConnected) return true;
      const actions = doc.getElementById('buy-actions');
      if (!actions) return false;
      const section = doc.createElement('section');
      section.id = 'ds-engraving';
      section.className = 'ds-engraving';
      section.setAttribute('aria-labelledby', 'ds-engraving-label');
      section.innerHTML = '<div class="a-field-heading"><label id="ds-engraving-label" for="ds-engraving-text">Engraving / Customization <small>(optional)</small></label><button type="button" class="a-link ds-engraving-clear" id="ds-engraving-clear" hidden>Clear</button></div>' +
        '<input id="ds-engraving-text" type="text" name="ds_engraving_text" autocomplete="off" autocapitalize="off" spellcheck="false" placeholder="Your words, made personal" aria-describedby="ds-engraving-help ds-engraving-tally ds-engraving-error">' +
        '<p id="ds-engraving-help" class="ds-engraving-help">$10 per character. Spaces are free.</p>' +
        '<div class="ds-engraving-tally" id="ds-engraving-tally" role="status" aria-live="polite" aria-atomic="true"><span id="ds-engraving-count">0 paid characters</span><strong id="ds-engraving-fee">$0.00</strong></div>' +
        '<p class="ds-engraving-note">Engraving is included in your total below and at checkout. Engraved pieces also have a 60-day return period.</p>' +
        '<p class="a-error ds-engraving-error" id="ds-engraving-error" role="alert" hidden></p>';
      actions.before(section);
      field = doc.getElementById('ds-engraving-text');
      field.addEventListener('input', function () { renderFeedback(true); });
      doc.getElementById('ds-engraving-clear').addEventListener('click', function () { field.value = ''; renderFeedback(true); field.focus(); });
      renderFeedback(true);
      if (observer) observer.disconnect();
      return true;
    }
    function start() {
      if (mount()) return;
      observer = new win.MutationObserver(mount);
      observer.observe(doc.body, { childList: true, subtree: true });
    }
    win.DSEngraving = { getSelection: getSelection, mount: mount, focus: function () { field?.focus(); field?.scrollIntoView({ block: 'center' }); } };
    doc.addEventListener('ds:selection', function () { mount(); });
    if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', start, { once: true });
    else start();
  }

  return { countCharacters: countCharacters, evaluateSelection: evaluateSelection, install: install };
});
